import React, { useState, useEffect, useRef } from 'react';
import { Select, notification } from 'antd';
import { useHistory, useParams } from 'react-router-dom';
import {
    MISC_KEYS,
    amIDoctorOnly,
    getDoc,
    getDocAccounts,
    getItemFromMisc,
    getMe,
    getMyClinics,
    getSelectedClinicId,
    updateItemInMisc,
} from '../../store/idbHandler';
import { AbhaIconNotLinked, businessLogo, clinicIcon } from './Icons';
import { AntTooltip } from './AntTooltip';
import RegisterInABHAAndEarn from '../../api/RegisterInABHAAndEarn';
import { Events, HPR_HFR_ONBOARDING, ON_HFR_CREATED_SUCCESSFULLY } from '../../bridge/events/types';
import { useDispatch } from 'react-redux';
import { fetchMyGenie } from '../../store/actions/login';
import { track } from '../../tracking';
import pushRegisterABDMLead from '../../api/pushRegisterABDMLead';
import { getMyDbId } from '../../api/mactiveid';
import { getAclViewClassName } from '../../restrictions/utils';
import { EKA_EVENT, dispatch as ekaDispatch } from '../../bridge/events/dispatch';
import { GenieSettings } from '../../api/pushGenie';
import { captureError } from '../../pages/captureError';
import { pushGenieToBE } from '../../store/appReduxMiddleware';
import { GO_TO_ABHA_DASHBOARD, GO_TO_ALL_PATIENTS } from '../../routes/paths';
import { his } from '../../pages/history';

const { Option } = Select;

const ClinicSelector: React.FunctionComponent<{
    currClinicId?: string;
    onClinicSelect: (selectedClinicId: string) => void;
    className?: string;
    showOnlyAbhaClinics?: boolean;
}> = ({ currClinicId, onClinicSelect, className, showOnlyAbhaClinics }) => {
    const history = useHistory();
    const dispatch = useDispatch();
    const { cId } = useParams<{ cId: string }>();
    const clinicId = cId ?? currClinicId;

    // const doctorProfile = getDoc()?.profile;
    const me = getMe();
    // const { professional } = doctorProfile || {};
    const clinics: { _id: string; name: string; abdm?: { hfr_id?: string } }[] =
        me?.clinics?.map((c) => ({ ...c, _id: c.id })) || [];

    //Commenting fallback in case required later
    // if (!me?.clinics?.length) {
    //     clinics = getMyClinics()?.map((item) => ({ ...item, id: item._id })) || [];
    // }

    const finalClinics = showOnlyAbhaClinics ? clinics.filter((c) => c.abdm?.hfr_id) : clinics;
    const [selectedClinic, setSelectedClinic] = useState(
        finalClinics.find((clinic) => clinic._id === clinicId) || null,
    );

    const handleClinicSelect = (clinic: {
        _id: string;
        name: string;
        abdm?: { hfr_id?: string };
    }) => {
        setSelectedClinic(clinic);
        onClinicSelect(clinic._id);
    };

    const enableAbha = async () => {
        const settings = await getItemFromMisc<GenieSettings>(
            getMyDbId() || '',
            MISC_KEYS.settings,
        );
        await updateItemInMisc(getMyDbId() || '', MISC_KEYS.settings, {
            ...settings,
            abdm: true,
        });

        try {
            await pushGenieToBE(getMyDbId() || '', ['settings']);
        } catch (err) {
            captureError(err as Error);
            notification.error({
                message: 'Error while saving',
                duration: 5,
            });
        }
    };

    const onEkaEvents: EventListener = async (event: any) => {
        const { detail } = event as CustomEvent<Events>;
        track('ABDM_CREATED_EVENT_RECEIVED', { detail: JSON.stringify(detail) });
        switch (detail.type) {
            case ON_HFR_CREATED_SUCCESSFULLY:
                track('ABDM_HFR_CREATED_EVENT_RECEIVED');
                await enableAbha();
                const docId = getDoc()?._id;
                if (!docId) {
                    return;
                }
                dispatch(
                    fetchMyGenie(undefined, undefined, () => {
                        track('ABDM_HFR_CREATED');
                        notification.success({
                            message: 'Successfully created ABDM!',
                            duration: 5,
                        });
                        his.replace(GO_TO_ALL_PATIENTS(getSelectedClinicId() || ''));
                    }),
                );
                break;
        }
    };

    const registerNow = () => {
        if (finalClinics?.find((c) => c.abdm?.hfr_id)) {
            pushRegisterABDMLead().catch(captureError);
            track('REGISTER_NOW_ABDM');
            ekaDispatch({ type: HPR_HFR_ONBOARDING });
        } else {
            history.push(GO_TO_ABHA_DASHBOARD);
        }
    };

    useEffect(() => {
        window.addEventListener(EKA_EVENT, onEkaEvents);
        return () => {
            window.removeEventListener(EKA_EVENT, onEkaEvents);
        };
    }, []);
    const selectRef = useRef<any>(null);

    // Handle dropdown visibility change
    const onDropdownVisibleChange = () => {
        if (selectRef.current) {
            selectRef.current.blur();
        }
    };

    return (
        <div className={`flex gap-8 items-center ${className || ''} `}>
            <Select
                ref={selectRef}
                showSearch
                value={selectedClinic?._id}
                placeholder="Select a clinic"
                onChange={(value: string) => {
                    const selectedClinic = finalClinics.find((clinic) => clinic._id === value);
                    if (selectedClinic) {
                        handleClinicSelect(selectedClinic);
                    }
                }}
                notFoundContent={null}
                filterOption={(input, option) => {
                    const clinicName = (option?.props?.children?.props?.children?.[0]?.props
                        ?.children?.[1]?.props?.children as unknown) as string;
                    return clinicName.toLowerCase().includes(input.toLowerCase());
                }}
                className="w-200 ant-selector-custom-styles cursor-pointers"
                onBlur={(e) => {
                    e.target.blur();
                }}
                dropdownStyle={{ minWidth: 'fit-content' }}
                onDropdownVisibleChange={onDropdownVisibleChange}
            >
                {finalClinics.map((clinic) => (
                    <Option key={clinic._id} value={clinic._id}>
                        <div className="flex items-center justify-between pt-2">
                            <div className="flex items-center">
                                <span className="clinic-icon">{clinicIcon('w-24 h-24')}</span>
                                <span className="ml-5 overflow-ellipsis text-13 overflow-hidden whitespace-nowrap dr-py-4 clinic-selector-truncate ">
                                    {clinic.name}
                                </span>
                            </div>
                            <span className={getAclViewClassName('ABHA')}>
                                {clinic?.abdm?.hfr_id ? (
                                    <AntTooltip content="ABDM Compliant">
                                        {AbhaIconNotLinked}
                                    </AntTooltip>
                                ) : amIDoctorOnly() ? (
                                    <RegisterInABHAAndEarn onRegister={registerNow} />
                                ) : (
                                    <AntTooltip
                                        className="dr-grayscale"
                                        content="Not ABDM Compliant"
                                    >
                                        {AbhaIconNotLinked}
                                    </AntTooltip>
                                )}
                            </span>
                        </div>
                    </Option>
                ))}
            </Select>
        </div>
    );
};

export default ClinicSelector;
